// Where each value sits in JSON text, by path, so validation issues (`model.entities.Blog.key`,
// `model.relationships[0].foreignKey`) can be shown at the right place in an editor.

export interface JsonRange {
  /** Start of the value, or of its key when it has one. */
  from: number;
  to: number;
}

/** Ranges of every value in `text`, keyed by path ('' is the whole document). Stops at the first syntax error. */
export function jsonPaths(text: string): Map<string, JsonRange> {
  const out = new Map<string, JsonRange>();
  let i = 0;
  const ws = () => {
    while (i < text.length && /\s/.test(text[i]!)) i++;
  };
  const string = (): string => {
    const start = i;
    i++;
    while (i < text.length && text[i] !== '"') i += text[i] === '\\' ? 2 : 1;
    i++;
    return JSON.parse(text.slice(start, i)) as string;
  };
  const expect = (c: string) => {
    ws();
    if (text[i] !== c) throw new Error(`expected ${c}`);
    i++;
  };
  const value = (path: string, keyFrom?: number) => {
    ws();
    const from = i;
    const c = text[i];
    if (c === '{') {
      i++;
      ws();
      if (text[i] === '}') i++;
      else
        for (;;) {
          ws();
          const keyStart = i;
          if (text[i] !== '"') throw new Error('expected a key');
          const key = string();
          expect(':');
          value(path ? `${path}.${key}` : key, keyStart);
          ws();
          if (text[i] === ',') {
            i++;
            continue;
          }
          expect('}');
          break;
        }
    } else if (c === '[') {
      i++;
      ws();
      if (text[i] === ']') i++;
      else
        for (let n = 0; ; n++) {
          value(`${path}[${n}]`);
          ws();
          if (text[i] === ',') {
            i++;
            continue;
          }
          expect(']');
          break;
        }
    } else if (c === '"') {
      string();
    } else {
      while (i < text.length && !/[,\]}\s]/.test(text[i]!)) i++;
      if (i === from) throw new Error('expected a value');
    }
    out.set(path, { from: keyFrom ?? from, to: i });
  };
  try {
    value('');
  } catch {
    // Keep what was found before the error.
  }
  return out;
}

/** The range of `path`, or of its closest ancestor that exists. */
export function rangeOf(paths: Map<string, JsonRange>, path: string): JsonRange | undefined {
  let p = path;
  for (;;) {
    const found = paths.get(p);
    if (found) return found;
    if (!p) return undefined;
    const cut = Math.max(p.lastIndexOf('.'), p.lastIndexOf('['));
    p = cut > 0 ? p.slice(0, cut) : '';
  }
}
